import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, EyeOff, Send, Save, Trash2, History } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ImageUploader } from '@/components/admin/ImageUploader';
import { GalleryUploader } from '@/components/admin/GalleryUploader';
import { LocationLintDialog } from '@/components/admin/LocationLintDialog';
import { VersionHistorySheet } from '@/components/admin/VersionHistorySheet';
import { adminApi } from '@/lib/api';
import { ENTRY_TYPES } from '@/lib/taxonomies';
import { useUnsavedGuard } from '@/lib/useUnsavedGuard';
import { useAutosave, autosaveLabel } from '@/lib/useAutosave';
import { ADMIN } from '@/constants/testIds';
import { useSeo } from '@/lib/seo';

const EMPTY = {
  title: '',
  entry_type: 'preseason',
  date: new Date().toISOString().slice(0, 10),
  summary: '',
  body: '',
  hero_image: { url: '', alt: '' },
  gallery: [],
  youtube_url: '',
  weather: '',
  wind: '',
  status: 'draft',
  slug: '',
};

export default function SeasonEditorPage() {
  useSeo({ title: 'Season Entry Editor | Admin' });
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState(EMPTY);
  const [initial, setInitial] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [lintOpen, setLintOpen] = useState(false);
  const [pendingPublish, setPendingPublish] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (isNew) return;
    adminApi.getSeason(id)
      .then((d) => {
        const next = {
          ...EMPTY,
          ...d,
          hero_image: d.hero_image || { url: '', alt: '' },
          gallery: d.gallery || [],
          date: (d.date || '').slice(0, 10),
        };
        setForm(next);
        setInitial(next);
      })
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  useUnsavedGuard(dirty);

  // Autosave: only for existing entries (needs id); preserves current status;
  // no toasts. UI shows a small "Saving…" / "Saved at HH:MM" label instead.
  const autoSaveNow = useCallback(async () => {
    if (isNew || !form.title.trim()) return null;
    try {
      const payload = { ...form };
      if (!payload.hero_image?.url) payload.hero_image = null;
      const out = await adminApi.updateSeason(id, payload);
      setInitial({ ...form, ...payload });
      return out;
    } catch {
      return null;
    }
  }, [isNew, id, form]);

  const autosave = useAutosave({
    enabled: !isNew && !!form.title.trim(),
    dirty,
    value: form,
    save: autoSaveNow,
    delayMs: 2000,
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Content-validation warnings (non-blocking)
  const missing = useMemo(() => {
    const out = [];
    if (!form.title.trim()) out.push('title');
    if (!form.summary.trim()) out.push('summary');
    if (!form.body.trim()) out.push('body');
    if (!form.hero_image?.url) out.push('hero image');
    if (!form.date) out.push('date');
    return out;
  }, [form]);

  const persist = async (statusOverride) => {
    if (!form.title.trim()) {
      toast.error('Title is required.');
      return null;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (statusOverride) payload.status = statusOverride;
      if (!payload.hero_image?.url) payload.hero_image = null;
      let out;
      if (isNew) {
        out = await adminApi.createSeason(payload);
        toast.success(payload.status === 'published' ? 'Published.' : 'Draft saved.');
        setInitial({ ...form, ...payload, id: out.id });
        nav(`/admin/season/${out.id}`, { replace: true });
      } else {
        out = await adminApi.updateSeason(id, payload);
        toast.success(
          payload.status === 'published'
            ? 'Published.'
            : payload.status === 'draft' && form.status === 'published'
            ? 'Unpublished.'
            : 'Draft saved.',
        );
        setInitial({ ...form, ...payload });
      }
      setForm((f) => ({ ...f, status: payload.status || f.status }));
      return out;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const onSaveDraft = () => persist('draft');
  const onPublishClick = () => {
    if (missing.length > 0) {
      const proceed = window.confirm(
        `Missing: ${missing.join(', ')}. Publish anyway?`,
      );
      if (!proceed) return;
    }
    setPendingPublish(true);
    setLintOpen(true);
  };
  const onPublishConfirm = async () => {
    setLintOpen(false);
    setPendingPublish(false);
    await persist('published');
  };
  const onUnpublish = async () => {
    if (!window.confirm('Unpublish this entry? It will become a draft.')) return;
    await persist('draft');
  };
  const onDuplicate = async () => {
    if (isNew) return;
    if (dirty && !window.confirm('You have unsaved changes. Duplicate the last saved version?')) return;
    try {
      const copy = await adminApi.duplicateSeason(id);
      toast.success('Duplicated as draft.');
      nav(`/admin/season/${copy.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Duplicate failed.');
    }
  };

  const save = (e) => {
    e?.preventDefault();
    return persist();
  };

  const remove = async () => {
    if (isNew) return;
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    try {
      await adminApi.deleteSeason(id);
      toast.success('Entry deleted.');
      nav('/admin/season');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed.');
    }
  };

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <form onSubmit={save} data-testid={ADMIN.seasonEditor} className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="kicker">{isNew ? 'New' : 'Edit'} Season Entry</p>
          <h1 className="font-serif text-3xl tracking-tight mt-1">
            {isNew ? 'Add a new entry.' : form.title || 'Untitled entry'}
          </h1>
        </div>
        <Link to="/admin/season">
          <Button type="button" variant="ghost">Cancel</Button>
        </Link>
      </div>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6 grid gap-5">
          <div>
            <Label className="kicker mb-2 block">Title</Label>
            <Input value={form.title} onChange={(e) => update({ title: e.target.value })} required />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label className="kicker mb-2 block">Entry type</Label>
              <Select value={form.entry_type} onValueChange={(v) => update({ entry_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTRY_TYPES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="kicker mb-2 block">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => update({ date: e.target.value })} />
            </div>
            <div>
              <Label className="kicker mb-2 block">Slug (optional)</Label>
              <Input value={form.slug || ''} onChange={(e) => update({ slug: e.target.value })} placeholder="auto" />
            </div>
          </div>
          <div>
            <Label className="kicker mb-2 block">Summary</Label>
            <Textarea rows={2} value={form.summary} onChange={(e) => update({ summary: e.target.value })} />
          </div>
          <div>
            <Label className="kicker mb-2 block">Body</Label>
            <Textarea rows={10} value={form.body} onChange={(e) => update({ body: e.target.value })} placeholder="Write the full narrative. Blank lines start new paragraphs." />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="kicker mb-2 block">Weather (optional)</Label>
              <Input value={form.weather || ''} onChange={(e) => update({ weather: e.target.value })} placeholder="Overcast, 52F" />
            </div>
            <div>
              <Label className="kicker mb-2 block">Wind (optional)</Label>
              <Input value={form.wind || ''} onChange={(e) => update({ wind: e.target.value })} placeholder="Light SW" />
            </div>
          </div>
          <div>
            <Label className="kicker mb-2 block">YouTube URL (optional)</Label>
            <Input value={form.youtube_url || ''} onChange={(e) => update({ youtube_url: e.target.value })} placeholder="https://youtu.be/…" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6">
          <ImageUploader
            label="Hero image"
            value={form.hero_image}
            onChange={(v) => update({ hero_image: v })}
          />
        </CardContent>
      </Card>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6">
          <GalleryUploader
            label="Gallery (optional)"
            value={form.gallery}
            onChange={(next) => update({ gallery: next })}
          />
        </CardContent>
      </Card>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant={form.status === 'published' ? 'default' : 'secondary'} className="uppercase tracking-wide">
                {form.status === 'published' ? 'Published' : 'Draft'}
              </Badge>
              {dirty && <span className="text-xs text-[hsl(38_65%_28%)]">Unsaved changes</span>}
              {!isNew && (
                <span
                  className={`text-xs ${
                    autosave.status === 'error'
                      ? 'text-[hsl(var(--destructive))]'
                      : autosave.status === 'saving'
                      ? 'text-[hsl(var(--muted-foreground))] animate-pulse'
                      : 'text-[hsl(var(--muted-foreground))]'
                  }`}
                  data-testid="admin-season-autosave-status"
                >
                  {autosaveLabel(autosave.status, autosave.lastSavedAt) || 'Autosave on'}
                </span>
              )}
              {missing.length > 0 && (
                <span className="text-xs text-[hsl(var(--muted-foreground))]">Missing: {missing.join(', ')}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {!isNew && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setHistoryOpen(true)}
                  data-testid="admin-season-history"
                >
                  <History size={14} className="mr-1.5" /> History
                </Button>
              )}
              {!isNew && (
                <Button type="button" variant="ghost" size="sm" onClick={onDuplicate} data-testid="admin-season-duplicate">
                  <Copy size={14} className="mr-1.5" /> Duplicate
                </Button>
              )}
              {!isNew && form.status === 'published' && (
                <Button type="button" variant="secondary" size="sm" onClick={onUnpublish} data-testid="admin-season-unpublish">
                  <EyeOff size={14} className="mr-1.5" /> Unpublish
                </Button>
              )}
              {!isNew && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button type="button" variant="ghost" size="sm" data-testid={ADMIN.seasonEditorDelete}>
                      <Trash2 size={14} className="mr-1.5" /> Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Removing “{form.title || 'Untitled'}” cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={remove}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={onSaveDraft}
                disabled={saving}
                data-testid="admin-season-save-draft"
              >
                <Save size={14} className="mr-1.5" /> Save draft
              </Button>
              <Button
                type="button"
                onClick={onPublishClick}
                disabled={saving}
                data-testid={ADMIN.seasonEditorSave}
              >
                <Send size={14} className="mr-1.5" /> {form.status === 'published' ? 'Update & keep published' : 'Publish'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <LocationLintDialog
        open={lintOpen}
        onCancel={() => { setLintOpen(false); setPendingPublish(false); }}
        onConfirm={onPublishConfirm}
        publishing={pendingPublish}
        fields={[
          { key: 'title', label: 'title', value: form.title },
          { key: 'summary', label: 'summary', value: form.summary },
          { key: 'body', label: 'body', value: form.body },
          { key: 'hero_alt', label: 'hero image alt text', value: form.hero_image?.alt },
          ...(form.gallery || []).map((g, i) => ({ key: `g${i}`, label: `gallery image ${i + 1} alt`, value: g.alt })),
        ]}
      />

      <VersionHistorySheet
        entryType="season"
        entryId={isNew ? null : id}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        onRestored={(entry) => {
          const next = {
            ...EMPTY,
            ...entry,
            hero_image: entry.hero_image || { url: '', alt: '' },
            gallery: entry.gallery || [],
            date: (entry.date || '').slice(0, 10),
          };
          setForm(next);
          setInitial(next);
        }}
      />
    </form>
  );
}
